import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import * as yup from 'yup';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { Card, DatePicker, Input, InputNumber, Select } from 'antd';
import style from './style.module.scss';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { RppAssetInput } from '../../components/rpp-asset-input';
import { BusinessUnitsSelectTree } from '../../components/region-selectors/business-units-select-tree';
import { useRppAdStore } from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import { useNavigation } from '../../utils/use-navigation';
import { RppAssetType } from '../../utils/rpp-assets';
import {
    RPP_AD_STATUSES,
    RppAd,
    RppAdForm,
    RppAdPayload,
} from '../../stores/rpp-ad';

const DATE_FORMAT = 'YYYY-MM-DD';

const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (value) {
            const action = this.resolve(yup.ref('action'));
            return !(value === undefined && action !== 'create');
        }),
});

type Params = yup.InferType<typeof YupParams>;

const YupForm = yup.object({
    title: yup.string().trim().required('Title is required.'),
    advertiser: yup.string(),
    videoKey: yup.string().required('A video must be uploaded before saving.'),
    durationSeconds: yup
        .number()
        .nullable()
        .positive('Duration must be a positive number.')
        .integer('Duration must be a whole number of seconds.'),
    status: yup.string().oneOf(RPP_AD_STATUSES).required(),
    startDate: yup.string().required('Start date is required.'),
    endDate: yup
        .string()
        .required('End date is required.')
        .test(
            'end-after-start',
            'End date must be on or after the start date.',
            function (endDate) {
                const startDate = this.parent.startDate;
                if (!startDate || !endDate) {
                    return true;
                }
                return endDate >= startDate;
            }
        ),
    provinceIds: yup
        .array()
        .of(yup.number().required())
        .min(1, 'Select at least one province.')
        .required(),
});

const INITIAL_FORM: RppAdForm = {
    title: '',
    advertiser: '',
    videoKey: '',
    durationSeconds: null,
    status: 'draft',
    startDate: '',
    endDate: '',
    provinceIds: [],
};

function adToForm(ad: RppAd): RppAdForm {
    return {
        title: ad.title,
        advertiser: ad.advertiser ?? '',
        videoKey: ad.videoKey,
        durationSeconds: ad.durationSeconds,
        status: ad.status,
        startDate: ad.startDate,
        endDate: ad.endDate,
        provinceIds: ad.provinceIds,
    };
}

function buildPayload(values: RppAdForm): RppAdPayload {
    const advertiser = values.advertiser.trim();
    return {
        title: values.title.trim(),
        advertiser: advertiser.length > 0 ? advertiser : null,
        videoKey: values.videoKey,
        durationSeconds: values.durationSeconds,
        status: values.status,
        startDate: values.startDate,
        endDate: values.endDate,
        provinceIds: values.provinceIds,
    };
}

// The province tree injects a string "SELECT_ALL" sentinel when every province
// is selected; keep only real numeric province ids before persisting.
function cleanProvinceIds(ids: number[]): number[] {
    return ids.filter((id) => Number.isInteger(id));
}

export const RppAdPage = observer(() => {
    const params = useParams<Params>();
    const store = useRppAdStore();
    const navigation = useNavigation();

    const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    const formik = useFormik<RppAdForm>({
        initialValues: INITIAL_FORM,
        validateOnMount: true,
        validationSchema: YupForm,
        onSubmit: () => void 0,
    });

    if (!YupParams.isValidSync(params)) {
        navigation.goToRppAds();
    }

    const persist = useCallback(async (): Promise<boolean> => {
        const payload = buildPayload(formik.values);
        const result =
            params.action === 'edit' && params.id
                ? await store.updateRppAd(Number(params.id), payload)
                : await store.createRppAd(payload);
        return result !== null;
    }, [formik.values, params.action, params.id, store]);

    // Used by the unsaved-changes modal: throw on failure so the user stays on
    // the page (keeping the already-uploaded videoKey) and can retry the save.
    const onModalSave = useCallback(async () => {
        const ok = await persist();
        if (!ok) {
            throw new Error('Failed to save RPP ad');
        }
    }, [persist]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: onModalSave,
    });

    const onSave = useCallback(async () => {
        const ok = await persist();
        if (ok) {
            clearDirty(() => navigation.goToRppAds());
        }
    }, [persist, clearDirty, navigation]);

    const onDeleteConfirm = useCallback(async () => {
        if (!params.id) {
            NotificationManager.showError("Can't delete this RPP ad.");
            return;
        }
        const response = await store.deleteRppAd(Number(params.id));
        if (!response.err) {
            clearDirty(() => navigation.goToRppAds());
        }
    }, [params.id, store, clearDirty, navigation]);

    useEffect(() => {
        if (params.action === 'edit' && params.id) {
            store
                .fetchRppAd(params.id)
                .then((ad) => {
                    if (!ad) {
                        throw new Error('RPP ad not found');
                    }
                    formik.resetForm({ values: adToForm(ad) });
                    setVideoPreviewUrl(ad.videoUrl);
                })
                .catch(() => navigation.goToRppAds());
        }
    }, []);

    const renderTopBarActions = () => {
        const saveDisabled = !formik.isValid || isUploading;
        if (params.action === 'edit') {
            return (
                <>
                    <DeleteButton
                        onClick={onDeleteConfirm}
                        loading={store.isDeleting}
                    />
                    <SaveButton
                        onClick={onSave}
                        loading={store.isSaving}
                        disabled={saveDisabled}
                    />
                </>
            );
        }
        return (
            <SaveButton
                onClick={onSave}
                loading={store.isSaving}
                disabled={saveDisabled}
            />
        );
    };

    const title =
        params.action === 'edit' && params.id
            ? `RPP Ad #${params.id}`
            : 'New RPP Ad';

    return (
        <>
            <PageTopBar
                title={title}
                goBack={{
                    callback: () => navigation.goToRppAds(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            <div className="content_with_margins">
                <Card>
                    <div className={style.field}>
                        <span className={style.label}>Title</span>
                        <Input
                            value={formik.values.title}
                            onChange={(e) =>
                                formik.setFieldValue('title', e.target.value)
                            }
                            placeholder="Summer RPP ad"
                        />
                    </div>

                    <div className={style.field}>
                        <span className={style.label}>Advertiser</span>
                        <Input
                            value={formik.values.advertiser}
                            onChange={(e) =>
                                formik.setFieldValue(
                                    'advertiser',
                                    e.target.value
                                )
                            }
                            placeholder="Vendor name (optional)"
                        />
                    </div>

                    <div className={style.field}>
                        <RppAssetInput
                            assetType={RppAssetType.AD_VIDEO}
                            label="Ad video"
                            value={formik.values.videoKey}
                            initialPreviewUrl={videoPreviewUrl}
                            onUploaded={(key) =>
                                formik.setFieldValue('videoKey', key)
                            }
                            onUploadingChange={setIsUploading}
                            disabled={store.isSaving}
                        />
                    </div>

                    <div className={style.field}>
                        <span className={style.label}>Duration (seconds)</span>
                        <InputNumber
                            className={style.control}
                            min={1}
                            value={formik.values.durationSeconds ?? undefined}
                            onChange={(value) =>
                                formik.setFieldValue(
                                    'durationSeconds',
                                    typeof value === 'number' ? value : null
                                )
                            }
                            placeholder="Optional"
                        />
                    </div>

                    <div className={style.field}>
                        <span className={style.label}>Status</span>
                        <Select
                            className={style.control}
                            value={formik.values.status}
                            onChange={(value) =>
                                formik.setFieldValue('status', value)
                            }
                        >
                            {RPP_AD_STATUSES.map((status) => (
                                <Select.Option key={status} value={status}>
                                    {status}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className={style.field}>
                        <span className={style.label}>Start date</span>
                        <DatePicker
                            className={style.control}
                            format={DATE_FORMAT}
                            value={
                                formik.values.startDate
                                    ? moment(
                                          formik.values.startDate,
                                          DATE_FORMAT
                                      )
                                    : null
                            }
                            onChange={(date) =>
                                formik.setFieldValue(
                                    'startDate',
                                    date ? date.format(DATE_FORMAT) : ''
                                )
                            }
                        />
                    </div>

                    <div className={style.field}>
                        <span className={style.label}>End date</span>
                        <DatePicker
                            className={style.control}
                            format={DATE_FORMAT}
                            value={
                                formik.values.endDate
                                    ? moment(formik.values.endDate, DATE_FORMAT)
                                    : null
                            }
                            onChange={(date) =>
                                formik.setFieldValue(
                                    'endDate',
                                    date ? date.format(DATE_FORMAT) : ''
                                )
                            }
                        />
                    </div>

                    <div className={style.field}>
                        <span className={style.label}>Provinces</span>
                        <BusinessUnitsSelectTree
                            value={formik.values.provinceIds}
                            onChange={(ids) =>
                                formik.setFieldValue(
                                    'provinceIds',
                                    cleanProvinceIds(ids)
                                )
                            }
                        />
                    </div>
                </Card>
            </div>
        </>
    );
});
